import type { PiletApi } from '@sharefiledev/sharefile-appshell';
import { storageExtensionSlots } from '@sharefiledev/storage-plugin-pilet-sdk';
import { configureHttp } from './api/apiClient';
import { addBlocks } from './blocks';
import {
	AsyncFileHomeTileExtensionSlot,
	AsyncLazyCount,
	AsyncLazyDownload,
	AsyncLazyEditDesktop,
	AsyncLazyEditOnline,
	AsyncLazyFilesTable,
	AsyncLazyFileViewerDrawer,
	AsyncLazyLinkToContainers,
	AsyncLazyMoveCopy,
	AsyncLazyNote,
	AsyncLazyPreviewFileViewerWrapperExtension,
	AsyncLazyProjectsTab,
	AsyncLazyRecentlyDeletedTab,
	AsyncLazyShareModal,
	AsyncLazyStorageZoneDropDown,
	AsyncLazyWorkflowsStorageZoneDropDown,
} from './components/extensions';
import { addModals } from './components/overlays';

export function setup(app: PiletApi) {
	// Project Files specific Tab
	app.registerExtension(storageExtensionSlots.ProjectTab, ({ params }) => (
		<AsyncLazyProjectsTab piletApi={app} {...params} />
	));

	// Recently Deleted Tab in Project
	app.registerExtension(storageExtensionSlots.RecentlyDeletedTab, ({ params }) => (
		<AsyncLazyRecentlyDeletedTab piletApi={app} {...params} />
	));

	// Customizable File Table Global Extension
	app.registerExtension(storageExtensionSlots.FilesTable, ({ params }) => (
		<AsyncLazyFilesTable piletApi={app} {...params} />
	));

	// Download Extension
	app.registerExtension(storageExtensionSlots.Download, ({ params }) => (
		<AsyncLazyDownload piletApi={app} {...params} />
	));

	// Edit Online Extension
	app.registerExtension(storageExtensionSlots.EditOnline, ({ params }) => (
		<AsyncLazyEditOnline piletApi={app} {...params} />
	));

	// Edit Desktop Extension
	app.registerExtension(storageExtensionSlots.EditDesktop, ({ params }) => (
		<AsyncLazyEditDesktop piletApi={app} {...params} />
	));

	// Share Extension
	app.registerExtension(storageExtensionSlots.ShareModal, ({ params }) => (
		<AsyncLazyShareModal piletApi={app} {...params} />
	));

	// Count Extension
	app.registerExtension(storageExtensionSlots.Count, ({ params }) => (
		<AsyncLazyCount piletApi={app} {...params} />
	));

	// FilesTile extension slot specific register to be shown on Home tab
	app.registerExtension(storageExtensionSlots.ProjectTile, ({ params }) => (
		<AsyncFileHomeTileExtensionSlot piletApi={app} {...params} />
	));

	// FileViewer Extension
	app.registerExtension(storageExtensionSlots.FileViewerExtension, ({ params }) => (
		<AsyncLazyPreviewFileViewerWrapperExtension piletApi={app} {...params} />
	));

	app.registerExtension(storageExtensionSlots.LinkToContainers, ({ params }) => (
		<AsyncLazyLinkToContainers piletApi={app} {...params} />
	));

	app.registerExtension(storageExtensionSlots.StorageZoneDropDownBlock, ({ params }) => (
		<AsyncLazyStorageZoneDropDown piletApi={app} {...params} />
	));
	app.registerExtension(storageExtensionSlots.StorageZoneDropDown, ({ params }) => (
		<AsyncLazyStorageZoneDropDown piletApi={app} {...params} />
	));

	// StorageZoneDropDown extension slot created for workflows support
	app.registerExtension(
		storageExtensionSlots.WorkflowsStorageZoneDropDownBlock,
		({ params }) => <AsyncLazyWorkflowsStorageZoneDropDown {...params} piletApi={app} />
	);
	app.registerExtension(
		storageExtensionSlots.WorkflowsStorageZoneDropDown,
		({ params }) => <AsyncLazyWorkflowsStorageZoneDropDown {...params} piletApi={app} />
	);

	// Move and copy Extension
	app.registerExtension(storageExtensionSlots.MoveCopy, ({ params }) => (
		<AsyncLazyMoveCopy piletApi={app} {...params} />
	));

	// Note Extension
	app.registerExtension(storageExtensionSlots.Note, ({ params }) => (
		<AsyncLazyNote piletApi={app} {...params} />
	));

	app.registerDrawer(
		storageExtensionSlots.FileViewerDrawer,
		({ options, completeInteraction }) => {
			const {
				containerRID = '',
				fileRID = '',
				...restOptions
			} = options as typeof options & { containerRID?: string; fileRID?: string };
			return (
				<AsyncLazyFileViewerDrawer
					piletApi={app}
					containerRID={containerRID}
					fileRID={fileRID}
					onCompleteInteraction={completeInteraction}
					{...restOptions}
				/>
			);
		},
		{
			drawerProps: {
				width: '100%',
				height: '100%',
				placement: 'bottom',
				closeIcon: null,
				styles: {
					body: {
						padding: 0,
						overflow: 'hidden',
					},
				},
				zIndex: 9,
			},
		}
	);

	addModals(app);
	addBlocks(app);

	if (process.env.NODE_ENV === 'development') {
		if (typeof app.meta.config.apiOrigin === 'string') {
			configureHttp((config, apiClient) => {
				config.origin = app.meta.config.apiOrigin;
				apiClient.defaults.baseURL = app.meta.config.apiOrigin + config.apiPath;
			});
		}
	}
}
